/**
 * Reason-coded team join-code denial copy + linkage verification (U3, R4/R5).
 *
 * Single source for the denial wording used by the join-code routes, the
 * client-side reason-code mapping, and tests. The "already set up under a
 * different account" claim is EARNED: routes may only send it after
 * classifyJoinCodeAthleteLinkage confirms another linkage actually exists —
 * pinned to the validated join code's org, and ignoring guardians revoked
 * from the athlete's active household. Denial responses never include an
 * email address.
 *
 * Client-safe: the only static value import is the shared denial sentence.
 * The server-only classifier lazy-imports its runtime dependencies.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { EnrollmentPermissionReasonCode } from '@/utils/family/enrollmentPermission';
import { CANNOT_REGISTER_ATHLETE_MESSAGE } from '@/utils/family/registrationPermissionCopy';

export const ATHLETE_LINKED_ELSEWHERE_MESSAGE =
  'This athlete is already set up under a different account at your gym. Sign in with that account, or ask the adult who holds it to invite you from their Family Access page.';

export const ATHLETE_NOT_IN_YOUR_ACCOUNT_MESSAGE =
  "This athlete isn't connected to your login yet. Ask your gym to send you an invitation for this athlete.";

export const ENROLLMENT_PERMISSION_DENIED_MESSAGE = CANNOT_REGISTER_ATHLETE_MESSAGE;

export const PERMISSION_CHECK_FAILED_MESSAGE =
  "We couldn't check that just now. Please try again in a minute.";

/**
 * Wire reasonCode → parent-facing copy. The `satisfies` clause pins every key
 * to the ENROLLMENT_PERMISSION_REASON_CODES value space (KTD3: one code space,
 * no parallel enum) without pulling that server module into client bundles.
 */
export const JOIN_DENIAL_MESSAGES = {
  athlete_linked_elsewhere: ATHLETE_LINKED_ELSEWHERE_MESSAGE,
  athlete_not_in_your_account: ATHLETE_NOT_IN_YOUR_ACCOUNT_MESSAGE,
  enrollment_permission_denied: ENROLLMENT_PERMISSION_DENIED_MESSAGE,
  permission_check_failed: PERMISSION_CHECK_FAILED_MESSAGE,
} as const satisfies Partial<Record<EnrollmentPermissionReasonCode, string>>;

export type JoinDenialReasonCode = keyof typeof JOIN_DENIAL_MESSAGES;

/**
 * Map a response `reasonCode` to the shared denial copy. Returns null for an
 * unknown/absent code so callers fall back to the server `error` string
 * verbatim (today's behavior for every non-reason-coded error path).
 */
export function resolveJoinDenialMessage(reasonCode: unknown): string | null {
  if (typeof reasonCode !== 'string') return null;
  return (JOIN_DENIAL_MESSAGES as Record<string, string>)[reasonCode] ?? null;
}

export type JoinCodeAthleteLinkage =
  | { kind: 'athlete_not_found' }
  | { kind: 'check_failed' }
  | { kind: 'linked_elsewhere' }
  | { kind: 'not_in_your_account' };

/**
 * Verification read behind the reason-coded join denials (server-only caller).
 * `organizationId` MUST be the validated join code's org: the athlete needs an
 * athlete_organizations row for it before ANY classification read runs, and a
 * missing row answers 'athlete_not_found' — so a cross-org athleteId probe is
 * indistinguishable from a nonexistent one (no existence/linkage oracle).
 * 'linked_elsewhere' is returned ONLY when the athlete row carries a different
 * auth_user_id or another NON-REVOKED auth user holds a
 * parent_athlete_relationships row (a guardian revoked from the athlete's
 * active household keeps a historical row but does not earn the claim).
 * Any read error (or a result contradicting the denial that brought us here)
 * is 'check_failed' — the route must answer 500 + retry copy, never a claim.
 */
export async function classifyJoinCodeAthleteLinkage(
  supabaseAdmin: SupabaseClient,
  authUserId: string,
  athleteId: string,
  organizationId: string
): Promise<JoinCodeAthleteLinkage> {
  const { data: athleteOrg, error: athleteOrgError } = await supabaseAdmin
    .from('athlete_organizations')
    .select('id')
    .eq('athlete_id', athleteId)
    .eq('organization_id', organizationId)
    .maybeSingle();

  if (athleteOrgError) return { kind: 'check_failed' };
  if (!athleteOrg) return { kind: 'athlete_not_found' };

  const { data: athlete, error: athleteError } = await supabaseAdmin
    .from('athletes')
    .select('id, auth_user_id')
    .eq('id', athleteId)
    .maybeSingle();

  if (athleteError) return { kind: 'check_failed' };
  if (!athlete) return { kind: 'athlete_not_found' };

  const athleteAuthUserId = (athlete as { auth_user_id: string | null }).auth_user_id;
  if (athleteAuthUserId === authUserId) {
    // The athlete IS linked to the caller, contradicting the denial that led
    // here (likely a transient upstream read problem). Be honest rather than
    // claim a different account exists.
    return { kind: 'check_failed' };
  }
  if (athleteAuthUserId) return { kind: 'linked_elsewhere' };

  const { data: otherGuardians, error: relationshipError } = await supabaseAdmin
    .from('parent_athlete_relationships')
    .select('parent_id')
    .eq('athlete_id', athleteId)
    .neq('parent_id', authUserId);

  if (relationshipError) return { kind: 'check_failed' };

  const otherParentIds = Array.from(
    new Set(
      ((otherGuardians ?? []) as Array<{ parent_id: string | null }>)
        .map((row) => row.parent_id)
        .filter((parentId): parentId is string => typeof parentId === 'string' && parentId.length > 0)
    )
  );
  if (otherParentIds.length === 0) return { kind: 'not_in_your_account' };

  const hasActive = await hasActiveOtherGuardian(supabaseAdmin, athleteId, otherParentIds);
  if (hasActive === null) return { kind: 'check_failed' };
  return hasActive ? { kind: 'linked_elsewhere' } : { kind: 'not_in_your_account' };
}

/**
 * Family Access revoke keeps the historical parent_athlete_relationships row
 * (support ticket #93), so a revoked guardian must not earn the "already set
 * up under a different account" claim. Mirrors the semantics of
 * filterRevokedFamilyGuardiansForAthlete in
 * utils/relationships/parentAthleteRelationships.ts: flag off, no active
 * household, or no family_guardians rows for a guardian → the relationship
 * rows stand. Unlike that display path (which fails OPEN so a read problem
 * never hides a real parent), this feeds a denial CLAIM — a read error fails
 * closed (returns null → 'check_failed').
 */
async function hasActiveOtherGuardian(
  supabaseAdmin: SupabaseClient,
  athleteId: string,
  otherParentIds: string[]
): Promise<boolean | null> {
  // Dynamic import keeps this module's static surface client-safe.
  const { FEATURE_FLAGS } = await import('@/utils/config/featureFlags');
  if (!FEATURE_FLAGS.ENABLE_FAMILY_GUARDIAN_ACCESS) return true;

  const { data: familyAthletes, error: familyAthletesError } = await supabaseAdmin
    .from('family_athletes')
    .select('family_account_id')
    .eq('athlete_id', athleteId)
    .eq('status', 'active');

  if (familyAthletesError) return null;

  const familyAccountIds = Array.from(
    new Set(
      ((familyAthletes ?? []) as Array<{ family_account_id: string | null }>)
        .map((row) => row.family_account_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    )
  );
  if (familyAccountIds.length === 0) return true;

  const { data: guardians, error: guardiansError } = await supabaseAdmin
    .from('family_guardians')
    .select('guardian_auth_user_id, status')
    .in('family_account_id', familyAccountIds)
    .in('guardian_auth_user_id', otherParentIds);

  if (guardiansError) return null;

  const statusesByGuardian = new Map<string, Set<string>>();
  for (const guardian of (guardians ?? []) as Array<{
    guardian_auth_user_id: string | null;
    status: string | null;
  }>) {
    if (!guardian.guardian_auth_user_id || !guardian.status) continue;
    const statuses = statusesByGuardian.get(guardian.guardian_auth_user_id) ?? new Set<string>();
    statuses.add(guardian.status);
    statusesByGuardian.set(guardian.guardian_auth_user_id, statuses);
  }

  return otherParentIds.some((parentId) => {
    const statuses = statusesByGuardian.get(parentId);
    return !statuses || statuses.has('active');
  });
}

/**
 * Translate a linkage classification into the reason-coded response payload.
 * Returns null for 'athlete_not_found' so each route keeps its legacy
 * status/body (no reason-coded copy asserting anything exists).
 */
export function resolveJoinCodeLinkageDenialPayload(
  linkage: JoinCodeAthleteLinkage
): { status: 403 | 500; body: { error: string; reasonCode: JoinDenialReasonCode } } | null {
  switch (linkage.kind) {
    case 'check_failed':
      return {
        status: 500,
        body: { error: PERMISSION_CHECK_FAILED_MESSAGE, reasonCode: 'permission_check_failed' },
      };
    case 'linked_elsewhere':
      return {
        status: 403,
        body: { error: ATHLETE_LINKED_ELSEWHERE_MESSAGE, reasonCode: 'athlete_linked_elsewhere' },
      };
    case 'not_in_your_account':
      return {
        status: 403,
        body: { error: ATHLETE_NOT_IN_YOUR_ACCOUNT_MESSAGE, reasonCode: 'athlete_not_in_your_account' },
      };
    default:
      return null;
  }
}
